import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { DeliveryNoteDuplicateSummary } from '../../../services/goods-receiving/goods-receiving.types';
import { formatDate } from '../../../utils/date-formatter';

interface DuplicateDeliveryDialogProps {
  open: boolean;
  duplicates: DeliveryNoteDuplicateSummary[];
  onClose: () => void;
  onProceed: () => void;
  isLoading?: boolean;
}

export function DuplicateDeliveryDialog({
  open,
  duplicates,
  onClose,
  onProceed,
  isLoading = false,
}: DuplicateDeliveryDialogProps) {
  const { t } = useTranslation('goodsReceiving');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('duplicate.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning">{t('duplicate.body', { count: duplicates.length })}</Alert>
          <List dense disablePadding>
            {duplicates.map((d) => (
              <ListItem key={d.id} disableGutters sx={{ alignItems: 'flex-start' }}>
                <ListItemText
                  primary={
                    <Typography variant="body2" component="div">
                      {t('duplicate.itemLabel', {
                        number: d.documentNumber ?? '-',
                        supplier: d.supplierName ?? '-',
                        date: formatDate(d.date),
                      })}
                    </Typography>
                  }
                  secondary={
                    <Chip
                      size="small"
                      label={
                        d.status === 'COMPLETED'
                          ? t('duplicate.statusCompleted')
                          : t('duplicate.statusDraft')
                      }
                      color={d.status === 'COMPLETED' ? 'success' : 'default'}
                      sx={{ mt: 0.5 }}
                    />
                  }
                  slotProps={{ secondary: { component: 'div' } }}
                />
              </ListItem>
            ))}
          </List>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Button onClick={onClose} disabled={isLoading} fullWidth sx={{ minHeight: 48 }}>
          {t('duplicate.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onProceed}
          disabled={isLoading}
          fullWidth
          sx={{ minHeight: 48 }}
        >
          {isLoading ? <CircularProgress size={20} /> : t('duplicate.proceed')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
